import React, {useState, useEffect} from "react";
import {Link} from "react-router-dom";
import {ArrowRight} from "lucide-react";
import axios from "axios";
import DOMPurify from "dompurify";
import Hero from "../components/Hero";
import CurrentProjects from "../components/CurrentProjects";
import floralBg from "../assets/floralbg.webp";
import hp2 from "../assets/hp2.webp";
import ambrosia2 from "../assets/ambrosia2.webp";
import girlWriting from "../assets/signing2.webp";

interface ContentSections {
  meet_hallie?: {title: string | null; content: string};
  newest_release_title?: {title: string | null; content: string};
  newest_release_description?: {title: string | null; content: string};
}

const Home: React.FC = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribeMessage, setSubscribeMessage] = useState("");
  const [contentSections, setContentSections] = useState<ContentSections>({});
  const [contentLoading, setContentLoading] = useState(true);

  useEffect(() => {
    fetchContentSections();
  }, []);

  const fetchContentSections = async () => {
    try {
      const response = await axios.get("/api/content-sections");
      if (response.data.success) {
        setContentSections(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching content sections:", error);
    } finally {
      setContentLoading(false);
    }
  };

  const handleNewsletterSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubscribing(true);
    setSubscribeMessage("");

    try {
      const response = await axios.post("/api/newsletter/subscribe", {
        email,
        name,
      });
      setSubscribeMessage(response.data.message);
      setEmail("");
      setName("");
    } catch (error: any) {
      setSubscribeMessage(error.response?.data?.error || "Failed to subscribe");
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <div className="space-y-16">
      {/* Hero Section */}
      <Hero />

      {/* Meet Hallie Section */}
      <section
        className="py-32 bg-cover bg-center bg-no-repeat relative overflow-hidden"
        style={{
          backgroundImage: `url(${floralBg})`,
        }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-primary-100 opacity-80"></div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left side - Text box */}
            <div className="bg-primary-300 border-4 border-primary-600 px-8 py-16 sm:px-16 sm:py-16 shadow-xl">
              <h2 className="text-5xl font-bold text-primary-400 mb-6">
                Meet Hallie
              </h2>
              <div
                className="text-lg text-primary-400 mb-8 leading-relaxed prose prose-lg max-w-none prose-primary"
                dangerouslySetInnerHTML={{
                  __html: contentLoading
                    ? "Loading..."
                    : DOMPurify.sanitize(
                        contentSections.meet_hallie?.content ||
                          "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua."
                      ),
                }}
              />
              <div className="text-center">
                <Link
                  to="/about"
                  className="btn inline-flex items-center bg-primary-700 text-primary-300 border-2 border-primary-200 hover:bg-primary-800 h-10 py-2 px-4"
                >
                  About the Author
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>

            {/* Right side - Author image */}
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-80 h-96 bg-primary-300 border-4 border-primary-200 rounded-[60%_40%_30%_70%/60%_30%_70%_40%] overflow-hidden shadow-lg">
                  <img
                    src={hp2}
                    alt="Hallie Parker"
                    width={320}
                    height={384}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Release Highlight */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left side - Book cover */}
            <div className="flex justify-center h-100">
              <div className="relative">
                <img
                  src={ambrosia2}
                  alt="The Untimely Revelation of the Gods"
                  width={320}
                  height={480}
                  loading="lazy"
                  decoding="async"
                  className="w-80 object-contain drop-shadow-xl"
                />
              </div>
            </div>

            {/* Right side - Book information box */}
            <div className="bg-primary-300 border-4 border-primary-600 px-8 py-12 shadow-lg">
              <h3 className="text-4xl text-primary-600 mb-4">Newest Release</h3>
              <div
                className="text-2xl font-bold text-primary-400 mb-6 prose prose-xl max-w-none prose-primary"
                dangerouslySetInnerHTML={{
                  __html: contentLoading
                    ? "Loading..."
                    : DOMPurify.sanitize(
                        contentSections.newest_release_title?.content ||
                          "The Untimely Revelation of the Gods"
                      ),
                }}
              />
              <div
                className="text-lg text-primary-800 mb-8 leading-relaxed prose prose-lg max-w-none prose-primary"
                dangerouslySetInnerHTML={{
                  __html: contentLoading
                    ? "Loading..."
                    : DOMPurify.sanitize(
                        contentSections.newest_release_description?.content ||
                          "The second installment of The Ambrosia Project series"
                      ),
                }}
              />
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/shop"
                  className="btn inline-flex items-center justify-center bg-primary-700 text-primary-300 border-2 border-primary-200 hover:bg-primary-800 h-10 py-2 px-4"
                >
                  Buy Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
                <Link
                  to="/projects"
                  className="btn inline-flex items-center justify-center bg-primary-700 text-primary-300 border-2 border-primary-200 hover:bg-primary-800 h-10 py-2 px-4"
                >
                  Read More
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Current Projects Section */}
      <CurrentProjects />

      {/* Newsletter Signup Section */}
      <section
        id="newsletter"
        className="py-16 bg-cover bg-center bg-no-repeat relative"
        style={{
          backgroundImage: `url(${girlWriting})`,
        }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-primary-100 opacity-80"></div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left side - Empty space for future content */}
            <div className="hidden md:block">
              {/* This space can be used for future content like testimonials, stats, etc. */}
            </div>

            {/* Right side - Newsletter signup box */}
            <div className="bg-primary-300 border-4 border-primary-600 px-8 py-12 shadow-lg">
              <h2 className="text-4xl font-bold text-primary-400 mb-6">
                Join the newsletter
              </h2>
              <p className="text-lg text-primary-800 mb-8 leading-relaxed">
                Be the first to read about new releases and exclusive previews.
              </p>

              <form onSubmit={handleNewsletterSignup} className="space-y-4">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-primary-800 mb-2"
                  >
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-primary-600 bg-white text-gray-900 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-primary-800 mb-2"
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-primary-600 bg-white text-gray-900 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="your@email.com"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubscribing}
                  className="w-full btn inline-flex items-center justify-center bg-primary-700 text-primary-300 border-2 border-primary-200 hover:bg-primary-800 h-10 py-2 px-4"
                >
                  {isSubscribing ? "Subscribing..." : "Subscribe"}
                </button>
              </form>

              {subscribeMessage && (
                <p
                  className={`mt-4 text-sm ${
                    subscribeMessage.includes("Failed") ||
                    subscribeMessage.includes("already")
                      ? "text-red-600"
                      : "text-green-600"
                  }`}
                >
                  {subscribeMessage}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
